import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

export default function VendrePage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    marque: '',
    modele: '',
    annee: '',
    kilometrage: '',
    carburant: '',
    boite: '',
    etat: '',
    prix_souhaite: '',
    description: '',
    disponibilite: '',
    forfait: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validation
    if (!formData.nom || !formData.prenom || !formData.email || !formData.telephone || 
        !formData.marque || !formData.modele || !formData.annee || !formData.kilometrage) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5hem03ihlvellpk524g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        // ✅ Envoyer les emails (admin + client)
        try {
          await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-vendre-notification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify(formData),
          });
          console.log('✅ Emails envoyés (admin + client)');
        } catch (emailError) {
          console.error('❌ Erreur envoi emails:', emailError);
        }

        setSubmitStatus('success');
        setFormData({
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          marque: '',
          modele: '',
          annee: '',
          kilometrage: '',
          carburant: '',
          boite: '',
          etat: '',
          prix_souhaite: '',
          description: '',
          disponibilite: '',
          forfait: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.louatiautocenter.ch';

  return (
    <div className="min-h-screen bg-dark-bg">
      <SeoHead
        title="Vendre mon Véhicule – Dépôt-Vente Auto Payerne | Louati AUTOCENTER"
        description="Confiez la vente de votre véhicule à Louati AUTOCENTER à Payerne. Service de dépôt-vente professionnel : estimation gratuite, photos pro, annonces, gestion complète. 3 formules disponibles."
        keywords="dépôt-vente voiture Payerne, vendre véhicule Suisse, estimation auto Payerne, vente voiture occasion"
        canonical={`${siteUrl}/vendre`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Vendre mon Véhicule – Dépôt-Vente Auto Payerne | Louati AUTOCENTER",
            "description": "Service de dépôt-vente professionnel à Payerne. Estimation gratuite, photos pro, gestion complète de la vente.",
            "url": `${siteUrl}/vendre`,
            "inLanguage": "fr-CH",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${siteUrl}/` },
                { "@type": "ListItem", "position": 2, "name": "Vendre mon véhicule", "item": `${siteUrl}/vendre` }
              ]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Comment fonctionne le dépôt-vente ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Vous nous confiez votre véhicule, nous le préparons, créons les annonces et gérons toute la vente jusqu'à la transaction finale. Vous ne payez qu'en cas de vente réussie."
                }
              },
              {
                "@type": "Question",
                "name": "Combien de temps pour vendre mon véhicule ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Le délai varie selon le modèle et le prix, mais en moyenne la vente se fait en 2 à 6 semaines."
                }
              },
              {
                "@type": "Question",
                "name": "Quels sont vos frais de dépôt-vente ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Nos frais varient selon la formule : CHF 1'000 fixe pour l'Essentiel, CHF 1'000 + 5% pour le Premium, ou 8% du prix de vente pour la Sérénité."
                }
              },
              {
                "@type": "Question",
                "name": "Puis-je récupérer mon véhicule si je change d'avis ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Oui, vous pouvez récupérer votre véhicule à tout moment avant la vente, selon les conditions de notre contrat de dépôt-vente."
                }
              }
            ]
          }
        ]}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-dark-card">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-serif text-5xl md:text-6xl font-bold mb-6 text-primary">
              Vendre mon Véhicule
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Vous souhaitez vendre votre véhicule sans stress et au meilleur prix ?
            </p>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Louati Autocenter vous accompagne dans toutes les étapes de la vente : estimation du prix, mise en valeur du véhicule, gestion des acheteurs et finalisation de la transaction.
            </p>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed mt-4">
              Notre objectif est simple : vous permettre de vendre votre véhicule rapidement et dans les meilleures conditions.
            </p>
          </div>
        </div>
      </section>

      {/* Forfaits Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-4">
              Nos Formules de Dépôt-Vente
            </h2>
            <p className="text-xl text-gray-400">
              Choisissez la formule adaptée à vos besoins
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Forfait 1 - Essentiel */}
            <div className="bg-dark-card border-2 border-dark-border rounded-2xl shadow-lg p-8 hover:shadow-2xl hover:border-primary/50 transition-all">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-car-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Dépôt-vente Essentiel
                </h3>
                <p className="text-gray-400">
                  Pour vendre votre véhicule simplement et efficacement.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-primary"></i>
                  Inclus
                </h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Estimation du prix du véhicule</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Photos et création de l'annonce</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Publication sur les plateformes de vente</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Gestion des appels et messages</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Organisation des visites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Gestion du contrat de vente</span>
                  </li>
                </ul>
              </div>

              <div className="border-t border-dark-border pt-6">
                <h4 className="font-semibold text-white mb-2">Tarification</h4>
                <p className="text-3xl font-bold text-primary mb-1">CHF 1'000</p>
                <p className="text-sm text-gray-400">Tarif fixe</p>
              </div>
            </div>

            {/* Forfait 2 - Premium */}
            <div className="bg-dark-card border-2 border-primary rounded-2xl shadow-2xl p-8 relative transform md:scale-105">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary text-dark-bg px-6 py-2 rounded-full text-sm font-semibold whitespace-nowrap">
                LE PLUS CHOISI
              </div>
              
              <div className="text-center mb-6 mt-2">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-vip-crown-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Dépôt-vente Premium
                </h3>
                <p className="text-gray-400">
                  Pour maximiser la visibilité et le prix de vente.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-primary"></i>
                  Inclus
                </h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Tout le pack Essentiel</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Nettoyage préparation vente</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Photos professionnelles du véhicule</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Mini vidéo du véhicule</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Optimisation de l'annonce</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Stratégie de vente optimisée</span>
                  </li>
                </ul>
              </div>

              <div className="border-t border-dark-border pt-6">
                <h4 className="font-semibold text-white mb-2">Tarification</h4>
                <p className="text-2xl font-bold text-primary mb-1">CHF 1'000 + 5%</p>
                <p className="text-sm text-gray-400">du prix de vente</p>
              </div>
            </div>

            {/* Forfait 3 - Sérénité */}
            <div className="bg-dark-card border-2 border-dark-border rounded-2xl shadow-lg p-8 hover:shadow-2xl hover:border-primary/50 transition-all">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-star-line text-3xl text-primary"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Dépôt-vente Sérénité
                </h3>
                <p className="text-gray-400">
                  Pour une gestion complète et zéro stress.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-primary"></i>
                  Inclus
                </h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Tout le pack Premium</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Gestion complète de la vente</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Organisation des éventuelles réparations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Préparation du véhicule pour la vente</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Possibilité de garantie pour l'acheteur</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-primary mt-0.5"></i>
                    <span>Accompagnement jusqu'à la livraison</span>
                  </li>
                </ul>
              </div>

              <div className="border-t border-dark-border pt-6">
                <h4 className="font-semibold text-white mb-2">Tarification</h4>
                <p className="text-3xl font-bold text-primary mb-1">8%</p>
                <p className="text-sm text-gray-400">du prix de vente</p>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-16">
            <a
              href="#formulaire"
              className="inline-flex items-center gap-3 px-8 py-4 bg-primary text-dark-bg rounded-lg font-semibold text-lg hover:bg-primary/90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-list-3-line text-xl"></i>
              Faire estimer mon véhicule
            </a>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="formulaire" className="py-20 bg-dark-card">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-dark-bg border border-dark-border rounded-2xl shadow-2xl p-8 md:p-12">
              <div className="mb-10 text-center">
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
                  Formulaire de Dépôt-Vente
                </h2>
                <p className="text-lg text-gray-400">
                  Remplissez ce formulaire et nous nous occupons de vendre votre véhicule au meilleur prix
                </p>
              </div>

              <form id="vendre-vehicule-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
                {/* Choix du Forfait */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                    <i className="ri-price-tag-3-line text-2xl text-primary"></i>
                    Choisissez votre Forfait
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="relative cursor-pointer">
                      <input
                        type="radio"
                        name="forfait"
                        value="Essentiel"
                        checked={formData.forfait === 'Essentiel'}
                        onChange={handleChange}
                        className="peer sr-only"
                      />
                      <div className="border-2 border-dark-border rounded-lg p-4 peer-checked:border-primary peer-checked:bg-primary/10 transition-all">
                        <div className="font-semibold text-white mb-1">Essentiel</div>
                        <div className="text-sm text-gray-400">CHF 1'000 fixe</div>
                      </div>
                    </label>
                    <label className="relative cursor-pointer">
                      <input
                        type="radio"
                        name="forfait"
                        value="Premium"
                        checked={formData.forfait === 'Premium'}
                        onChange={handleChange}
                        className="peer sr-only"
                      />
                      <div className="border-2 border-dark-border rounded-lg p-4 peer-checked:border-primary peer-checked:bg-primary/10 transition-all">
                        <div className="font-semibold text-white mb-1">Premium</div>
                        <div className="text-sm text-gray-400">CHF 1'000 + 5%</div>
                      </div>
                    </label>
                    <label className="relative cursor-pointer">
                      <input
                        type="radio"
                        name="forfait"
                        value="Sérénité"
                        checked={formData.forfait === 'Sérénité'}
                        onChange={handleChange}
                        className="peer sr-only"
                      />
                      <div className="border-2 border-dark-border rounded-lg p-4 peer-checked:border-primary peer-checked:bg-primary/10 transition-all">
                        <div className="font-semibold text-white mb-1">Sérénité</div>
                        <div className="text-sm text-gray-400">8% du prix</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Informations Personnelles */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                    <i className="ri-user-line text-2xl text-primary"></i>
                    Vos Informations
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="nom" className="block text-sm font-medium text-gray-400 mb-2">
                        Nom <span className="text-primary">*</span>
                      </label>
                      <input
                        type="text"
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                        placeholder="Votre nom"
                      />
                    </div>
                    <div>
                      <label htmlFor="prenom" className="block text-sm font-medium text-gray-400 mb-2">
                        Prénom <span className="text-primary">*</span>
                      </label>
                      <input
                        type="text"
                        id="prenom"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                        placeholder="Votre prénom"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                        Email <span className="text-primary">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                        placeholder="votre@email.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="telephone" className="block text-sm font-medium text-gray-400 mb-2">
                        Téléphone <span className="text-primary">*</span>
                      </label>
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                        placeholder="+41 XX XXX XX XX"
                      />
                    </div>
                  </div>
                </div>

                {/* Informations du Véhicule */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                    <i className="ri-car-line text-2xl text-primary"></i>
                    Informations du Véhicule
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="marque" className="block text-sm font-medium text-gray-400 mb-2">
                        Marque <span className="text-primary">*</span>
                      </label>
                      <input
                        type="text"
                        id="marque"
                        name="marque"
                        value={formData.marque}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                        placeholder="Ex: BMW, Mercedes, Audi..."
                      />
                    </div>
                    <div>
                      <label htmlFor="modele" className="block text-sm font-medium text-gray-400 mb-2">
                        Modèle <span className="text-primary">*</span>
                      </label>
                      <input
                        type="text"
                        id="modele"
                        name="modele"
                        value={formData.modele}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                        placeholder="Ex: Série 3, Classe C, A4..."
                      />
                    </div>
                    <div>
                      <label htmlFor="annee" className="block text-sm font-medium text-gray-400 mb-2">
                        Année <span className="text-primary">*</span>
                      </label>
                      <input
                        type="number"
                        id="annee"
                        name="annee"
                        value={formData.annee}
                        onChange={handleChange}
                        required
                        min="1990"
                        max="2025"
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                        placeholder="2020"
                      />
                    </div>
                    <div>
                      <label htmlFor="kilometrage" className="block text-sm font-medium text-gray-400 mb-2">
                        Kilométrage <span className="text-primary">*</span>
                      </label>
                      <input
                        type="number"
                        id="kilometrage"
                        name="kilometrage"
                        value={formData.kilometrage}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                        placeholder="50000"
                      />
                    </div>
                    <div>
                      <label htmlFor="carburant" className="block text-sm font-medium text-gray-400 mb-2">
                        Carburant
                      </label>
                      <select
                        id="carburant"
                        name="carburant"
                        value={formData.carburant}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="Essence">Essence</option>
                        <option value="Diesel">Diesel</option>
                        <option value="Hybride">Hybride</option>
                        <option value="Électrique">Électrique</option>
                        <option value="GPL">GPL</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="boite" className="block text-sm font-medium text-gray-400 mb-2">
                        Boîte de vitesses
                      </label>
                      <select
                        id="boite"
                        name="boite"
                        value={formData.boite}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="Manuelle">Manuelle</option>
                        <option value="Automatique">Automatique</option>
                        <option value="Semi-automatique">Semi-automatique</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* État et Prix */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-3">
                    <i className="ri-price-tag-3-line text-2xl text-primary"></i>
                    État et Prix
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="etat" className="block text-sm font-medium text-gray-400 mb-2">
                        État général
                      </label>
                      <select
                        id="etat"
                        name="etat"
                        value={formData.etat}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                      >
                        <option value="">Sélectionnez</option>
                        <option value="Excellent">Excellent</option>
                        <option value="Très bon">Très bon</option>
                        <option value="Bon">Bon</option>
                        <option value="Correct">Correct</option>
                        <option value="À rénover">À rénover</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="prix_souhaite" className="block text-sm font-medium text-gray-400 mb-2">
                        Prix souhaité (CHF)
                      </label>
                      <input
                        type="number"
                        id="prix_souhaite"
                        name="prix_souhaite"
                        value={formData.prix_souhaite}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                        placeholder="25000"
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-400 mb-2">
                    Description et équipements
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base resize-none text-white"
                    placeholder="Décrivez votre véhicule, ses équipements, son historique d'entretien, etc. (max 500 caractères)"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-2">{formData.description.length}/500 caractères</p>
                </div>

                {/* Disponibilité */}
                <div>
                  <label htmlFor="disponibilite" className="block text-sm font-medium text-gray-400 mb-2">
                    Disponibilité pour une visite
                  </label>
                  <input
                    type="text"
                    id="disponibilite"
                    name="disponibilite"
                    value={formData.disponibilite}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-dark-card border border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-base text-white"
                    placeholder="Ex: Du lundi au vendredi après 18h"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary text-dark-bg px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line text-2xl animate-spin"></i>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill text-2xl"></i>
                        Confier mon véhicule
                      </>
                    )}
                  </button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-primary/10 border border-primary rounded-lg p-4 flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-primary flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Demande envoyée avec succès !</h4>
                      <p className="text-sm text-gray-400">Nous vous contacterons rapidement pour organiser la prise en charge de votre véhicule.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 flex items-start gap-3">
                    <i className="ri-error-warning-fill text-2xl text-red-500 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Erreur lors de l'envoi</h4>
                      <p className="text-sm text-gray-400">Veuillez vérifier tous les champs obligatoires et réessayer.</p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-xl text-gray-400">
              Un service de dépôt-vente simple et efficace en 4 étapes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-file-list-3-line text-4xl text-primary"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">1. Estimation</h3>
              <p className="text-gray-400">Nous évaluons votre véhicule et définissons le prix de vente optimal</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-camera-line text-4xl text-primary"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">2. Préparation</h3>
              <p className="text-gray-400">Photos professionnelles et mise en valeur de votre véhicule</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-megaphone-line text-4xl text-primary"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">3. Commercialisation</h3>
              <p className="text-gray-400">Publication des annonces et gestion des acheteurs potentiels</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <i className="ri-hand-coin-line text-4xl text-primary"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">4. Finalisation</h3>
              <p className="text-gray-400">Contrat de vente et paiement sécurisé</p>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 bg-dark-card">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-4">
                Pourquoi nous confier votre véhicule ?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-dark-bg border border-dark-border rounded-xl p-6 shadow-lg hover:border-primary/50 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Gain de temps
                    </h3>
                    <p className="text-gray-400">
                      Nous gérons tout : annonces, appels, visites et négociations
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-dark-bg border border-dark-border rounded-xl p-6 shadow-lg hover:border-primary/50 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Sécurité
                    </h3>
                    <p className="text-gray-400">
                      Transaction sécurisée et gestion administrative complète
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-dark-bg border border-dark-border rounded-xl p-6 shadow-lg hover:border-primary/50 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-euro-circle-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Meilleur prix
                    </h3>
                    <p className="text-gray-400">
                      Notre expertise nous permet d'obtenir le meilleur prix du marché
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-dark-bg border border-dark-border rounded-xl p-6 shadow-lg hover:border-primary/50 transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-star-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Mise en valeur
                    </h3>
                    <p className="text-gray-400">
                      Photos professionnelles et présentation optimale de votre véhicule
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-4">
                Questions Fréquentes
              </h2>
            </div>

            <div className="space-y-6">
              <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-3">
                  <i className="ri-question-line text-xl text-primary"></i>
                  Comment fonctionne le dépôt-vente ?
                </h3>
                <p className="text-gray-400 pl-9">
                  Vous nous confiez votre véhicule, nous le préparons, créons les annonces et gérons toute la vente jusqu'à la transaction finale. Vous ne payez qu'en cas de vente réussie.
                </p>
              </div>

              <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-3">
                  <i className="ri-question-line text-xl text-primary"></i>
                  Combien de temps pour vendre mon véhicule ?
                </h3>
                <p className="text-gray-400 pl-9">
                  Le délai varie selon le modèle et le prix, mais nous mettons tout en œuvre pour vendre rapidement. En moyenne, la vente se fait en 2 à 6 semaines.
                </p>
              </div>

              <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-3">
                  <i className="ri-question-line text-xl text-primary"></i>
                  Quels sont vos frais ?
                </h3>
                <p className="text-gray-400 pl-9">
                  Nos frais varient selon la formule choisie : CHF 1'000 fixe pour l'Essentiel, CHF 1'000 + 5% pour le Premium, ou 8% du prix de vente pour la Sérénité.
                </p>
              </div>

              <div className="bg-dark-card border border-dark-border rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-3">
                  <i className="ri-question-line text-xl text-primary"></i>
                  Puis-je récupérer mon véhicule si je change d'avis ?
                </h3>
                <p className="text-gray-400 pl-9">
                  Oui, vous pouvez récupérer votre véhicule à tout moment avant la vente, selon les conditions de notre contrat de dépôt-vente.
                </p>
              </div>
            </div>

            {/* Bouton Retour en bas */}
            <div className="mt-12 text-center">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 border-2 border-primary text-primary rounded-lg font-semibold hover:bg-primary hover:text-dark-bg transition-colors whitespace-nowrap cursor-pointer"
              >
                Retour
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}